import React, { useState, useEffect, useRef } from 'react';
import { searchProducts, getStores } from '../utils/db';
import PriceComparison from '../components/PriceComparison';
import ProductCardGrid from '../components/ProductCardGrid';
import AisleView from '../components/AisleView';
import AislePage from './AislePage';
import SubcategoryPage from './SubcategoryPage';
import { AISLES, SUBCATEGORY_CONFIG } from '../utils/taxonomy';
import { logSearchMiss, getGroupedRequests, normalizeName } from '../components/requests/requestsHelpers';
import { trackSearch, trackSearchMiss, trackAisleOpen, trackSubcategoryOpen } from '../utils/analytics';
import RequestModal from '../components/requests/RequestModal';

import './BrowsePage.css';

// ── BrowsePage ────────────────────────────────────────────────────────────────
// Aisle-only browse: supermarket-style drill-down navigation.
// Search + filter catalogue has moved to ExplorePage.

// Normalise storeId: strip "store_" prefix added by some data paths
function normStoreId(storeId) {
  return storeId?.replace(/^store_/, '') ?? storeId;
}

function BrowsePage({ navKey, onNavigateToCategory, onAddToBasket, aisleExpandedCategory, onAisleExpandedCategoryChange, browseDeepLink, onBrowseDeepLinkConsumed }) {
  // Aisle-mode search
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);

  const [stores, setStores]               = useState([]);
  const [allProducts, setAllProducts]     = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);

  // Request modal
  const [showModal, setShowModal]           = useState(false);
  const [modalQuery, setModalQuery]         = useState('');
  const [requestedForTerm, setRequestedForTerm] = useState(''); // tracks which term user already submitted
  const [selectedProduct, setSelectedProduct]   = useState(null);

  const [aisleNav, setAisleNav]                 = useState(null);
  const [aislesWithCounts, setAislesWithCounts] = useState(AISLES.map(a => ({ ...a, count: 0 })));

  // Reset to aisle list when Browse tab is tapped (navKey increments each tap)
  const isFirstNavKey = useRef(true);
  useEffect(() => {
    if (isFirstNavKey.current) { isFirstNavKey.current = false; return; }
    setSelectedProduct(null);
    setAisleNav(null);
  }, [navKey]);

  // Deep-link from home screen quick-start chips
  useEffect(() => {
    if (!browseDeepLink) return;
    if (!browseDeepLink.aisleId) {
      setAisleNav(null);
    } else {
      const aisle = AISLES.find(a => a.id === browseDeepLink.aisleId);
      if (!aisle) return;
      if (browseDeepLink.subcategoryId) {
        const subcatConfig = SUBCATEGORY_CONFIG[browseDeepLink.subcategoryId];
        if (subcatConfig) setAisleNav({ level: 'subcategory', aisle, subcategory: { id: browseDeepLink.subcategoryId, ...subcatConfig } });
      } else {
        setAisleNav({ level: 'aisle', aisle });
      }
    }
    onBrowseDeepLinkConsumed?.();
  }, [browseDeepLink]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStores().then(setStores);
  }, []);

  useEffect(() => {
    searchProducts('').then(products => {
      setAllProducts(products);

      // One card per productId family — standard tier as representative.
      // allProducts retains every tier+variant doc for PriceComparison siblings.
      const CARD_TIER_PRIORITY = ['standard', 'basic', 'premium', 'organic', 'brand'];
      const seen = new Map();
      products.forEach(p => {
        const key = p.productId || p.id;
        if (!seen.has(key)) {
          seen.set(key, p);
        } else {
          const ePri = CARD_TIER_PRIORITY.indexOf(seen.get(key).tier ?? '');
          const nPri = CARD_TIER_PRIORITY.indexOf(p.tier ?? '');
          if (nPri !== -1 && (nPri < ePri || ePri === -1)) seen.set(key, p);
        }
      });
      const deduplicated = [...seen.values()];
      setDisplayProducts(deduplicated);

      // Aisle counts (deduplicated list)
      const aisleCounts = {};
      AISLES.forEach(a => { aisleCounts[a.id] = 0; });
      deduplicated.forEach(p => {
        const cat = p.category?.toLowerCase();
        AISLES.forEach(a => {
          if (!a.comingSoon && a.categories.includes(cat)) aisleCounts[a.id]++;
        });
      });
      setAislesWithCounts(AISLES.map(a => ({ ...a, count: aisleCounts[a.id] || 0 })));
    }).catch(err => console.error('Error loading products:', err));
  }, []);

  // ── Product selection — goes straight to PriceComparison ─────────────────
  const handleProductClick = (product) => setSelectedProduct(product);

  // ── Aisle progressive-disclosure navigation ────────────────────────────────
  const handleAisleSelect       = (aisle)       => { trackAisleOpen(aisle.name); setAisleNav({ level: 'aisle', aisle }); };
  const handleSubcategorySelect = (subcategory) => { trackSubcategoryOpen(subcategory); setAisleNav(prev => ({ level: 'subcategory', aisle: prev.aisle, subcategory })); };
  const handleBackToAisleList   = ()            => setAisleNav(null);
  const handleBackToAisle       = ()            => setAisleNav(prev => ({ level: 'aisle', aisle: prev.aisle }));

  // ── Aisle-mode search ─────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) { clearSearch(); return; }
    const results = await searchProducts(searchQuery);
    setSearchResults(results);
    setIsSearching(true);
    trackSearch(searchQuery.trim(), results.length);

    // Auto-capture search miss — silent, session-deduped
    if (results.length === 0) {
      logSearchMiss(searchQuery.trim());
      trackSearchMiss(searchQuery.trim());
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setRequestedForTerm('');
  };

  // ── Price comparison screen ───────────────────────────────────────────────
  if (selectedProduct) {
    const prices = (selectedProduct.prices || [])
      .filter(p => p.price > 0)
      .map(p => {
        const store = stores.find(s => s.id === p.storeId);
        const name  = store?.name || normStoreId(p.storeId).replace(/_/g, ' ');
        return { ...p, storeName: name };
      });
    const siblings = allProducts.filter(p => p.productId === selectedProduct.productId);
    const handleBackToAisleFromPC = () => {
      setSelectedProduct(null);
      setAisleNav(prev =>
        prev?.level === 'subcategory' ? { level: 'aisle', aisle: prev.aisle } : null
      );
    };

    return (
      <div className="browse-page">
        <PriceComparison
          product={selectedProduct}
          prices={prices}
          siblings={siblings}
          stores={stores}
          onBack={() => setSelectedProduct(null)}
          onAddToBasket={onAddToBasket}
          aisleContext={aisleNav}
          onBackToAisle={handleBackToAisleFromPC}
        />
      </div>
    );
  }

  // ── Aisle level: subcategory list ──────────────────────────────────────────
  if (aisleNav?.level === 'aisle') {
    const aisleProducts = displayProducts.filter(p =>
      aisleNav.aisle.categories.includes(p.category?.toLowerCase())
    );
    return (
      <div className="browse-page">
        <div className="browse-container">
          <AislePage
            aisle={aisleNav.aisle}
            products={aisleProducts}
            onSelectSubcategory={handleSubcategorySelect}
            onSelectProduct={handleProductClick}
            onAddToBasket={onAddToBasket}
            onBack={handleBackToAisleList}
          />
        </div>
      </div>
    );
  }

  // ── Subcategory level: product list ───────────────────────────────────────
  if (aisleNav?.level === 'subcategory') {
    const subcategoryProducts = displayProducts.filter(p =>
      aisleNav.aisle.categories.includes(p.category?.toLowerCase()) &&
      p.subcategory === aisleNav.subcategory.id
    );
    return (
      <div className="browse-page">
        <div className="browse-container">
          <SubcategoryPage
            subcategory={aisleNav.subcategory}
            aisle={aisleNav.aisle}
            products={subcategoryProducts}
            onSelectProduct={handleProductClick}
            onAddToBasket={onAddToBasket}
            onBack={handleBackToAisle}
            stores={stores}
          />
        </div>
      </div>
    );
  }

  // ── Aisle home: search bar + aisle grid ───────────────────────────────────
  return (
    <div className="browse-page">
      {/* Request modal — opened from empty search state */}
      <RequestModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => {
          setShowModal(false);
          setRequestedForTerm(searchQuery.trim());
        }}
        initialProductName={modalQuery}
      />
      <div className="browse-container">

        {/* Hint */}
        {!isSearching && (
          <p className="screen-hint">Browse by aisle or search by name.</p>
        )}

        {/* Aisle-mode search */}
        {!isSearching && (
          <form onSubmit={handleSearch} className="browse-search">
            <span className="search-icon-lead" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="9" cy="9" r="6"/>
                <line x1="14" y1="14" x2="18" y2="18"/>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search milk, butter, eggs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button type="button" className="clear-search" aria-label="Clear" onClick={clearSearch}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="1" y1="1" x2="11" y2="11"/>
                  <line x1="11" y1="1" x2="1" y2="11"/>
                </svg>
              </button>
            )}
            <button type="submit" className="search-button">Search</button>
          </form>
        )}

        {/* Search results */}
        {isSearching && (
          <>
            <div className="lv-search-header">
              <span className="lv-search-title">"{searchQuery}"</span>
              <button className="lv-back-btn" onClick={clearSearch}>← Back</button>
            </div>
            {searchResults.length > 0 ? (
              <ProductCardGrid
                products={searchResults}
                allProducts={allProducts}
                stores={stores}
                onSelectProduct={handleProductClick}
                onAddToBasket={onAddToBasket}
              />
            ) : (
              <div className="lv-empty">
                <p className="lv-empty-heading">No products found for "{searchQuery}"</p>
                {(() => {
                  // Check if this term has an approved/added status in admin
                  const groups = getGroupedRequests();
                  const normalized = normalizeName(searchQuery);
                  const group = groups.find(g => g.normalizedProductName === normalized);
                  const isComingSoon = ['approved', 'added'].includes(group?.status);
                  const alreadyRequested = requestedForTerm === searchQuery.trim();

                  if (isComingSoon) {
                    return (
                      <p className="lv-coming-soon-badge">Coming soon — we're working on adding this</p>
                    );
                  }
                  if (alreadyRequested) {
                    return (
                      <p className="lv-confirmed-msg">We'll look into adding this. Thanks for the suggestion.</p>
                    );
                  }
                  return (
                    <div className="lv-empty-actions">
                      <button
                        className="lv-request-btn"
                        onClick={() => {
                          setModalQuery(searchQuery);
                          setShowModal(true);
                        }}
                      >
                        Request this product →
                      </button>
                      <button className="lv-pill" onClick={clearSearch}>Clear search</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {/* Aisle navigation grid */}
        {!isSearching && (
          <AisleView
            aisles={aislesWithCounts}
            onSelectAisle={handleAisleSelect}
          />
        )}

      </div>
    </div>
  );
}

export default BrowsePage;
